import { useContext } from "react";
import { useQuery } from "@apollo/client";

import { ingredientsObjectSelector } from "../../app/commonSelector";
import { FETCH_PUBLIC_RECIPES, FETCH_RATINGS_SUMMARY_LAST_60_DAYS, GET_ALL_INGREDIENTS } from "../../app/graphql/queries";
import { setIngredientsList, setRecipeList } from "../../app/homepage/homepage.slice";
import { RatingsInterface, SearchFilteredRecipesInterface } from "../../app/search/search.types";
import { useAppDispatch, useAppSelector } from "../../app/store/hooks";
import { ErrorManagementContext } from "../../utils/errorManagement/ErrorManagementProvider";
import { transformedRecipes } from "../utils/useFIlterRecipe.utils";

const useFetchRecipesAndIngredients = () => {
  const dispatch = useAppDispatch();
  const { cookClubLog } = useContext(ErrorManagementContext);

  const ingredientsMap = useAppSelector(ingredientsObjectSelector);
  const ingredients = useAppSelector(state => state.homepage.ingredients);

  const { loading: fetchingIngredients } = useQuery(GET_ALL_INGREDIENTS, {
    skip: ingredients.length !== 0,
    onCompleted: (data) => {
      dispatch(setIngredientsList(data?.ingredients || []));
    }
  });

  const { loading: fetchingRatings, data: ratingsData } = useQuery(FETCH_RATINGS_SUMMARY_LAST_60_DAYS);

  const { loading: fetchingRecipes } = useQuery(FETCH_PUBLIC_RECIPES, {
    skip: ingredients.length === 0 || fetchingRatings,
    onCompleted: (data) => {
      const ratingsHashMap: Record<string, { rating: number | null, ratingCount: number | null }> = {};
      ratingsData?.ratingsSummaryLast60DaysView.forEach((rating: RatingsInterface) => {
        ratingsHashMap[rating.recipeID] = {
          rating: rating.rating || null,
          ratingCount: rating.ratingCount || null,
        };
      });

      const transRecipes = transformedRecipes(data?.Recipes_E3, ingredientsMap);
      const recipesWithRatings = transRecipes.map((recipe: SearchFilteredRecipesInterface) => {
        const { rating, ratingCount } = ratingsHashMap[recipe.id] || { rating: null, ratingCount: null };

        return {
          ...recipe,
          metadata: {
            ...recipe.metadata,
            rating,
            ratingCount,
          }
        };
      });

      dispatch(setRecipeList(recipesWithRatings));
    },
    onError: (err) => {
      cookClubLog(
        "",
        "Unable to fetch Recipes!",
        '',
        { ui: { type: "error" }, browserConsole: true }
      );
      console.error('Error fetching the recipes:', err);
    }
  });

  return { loading: fetchingRecipes || fetchingIngredients || fetchingRatings };
};

export default useFetchRecipesAndIngredients;
